import { useContext, useMemo, useSyncExternalStore } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  ColumnDataTypes,
  type ColumnConfig,
  type TableRowData,
  type CustomCellContext,
} from '../types';
import { getLeafColumns } from '../utils/columnGroupUtils';
import { TimestampFormatContext } from '@/contexts/TimestampFormatContext';
import { getTimestampColumnWidth } from '@/components/shared/timestampColumnWidth';
import {
  getDisplayTimeZone,
  subscribeDisplayTimeZone,
} from '@/lib/displayTimeZone';

/**
 * Converts ColumnConfig definitions to TanStack Table ColumnDef format.
 *
 * When a column has children, the leaf columns are flattened into individual
 * TanStack columns (since they define accessor and renderCell for cells).
 * Group headers are rendered separately by TableHeader.
 *
 * @example
 * ```tsx
 * const columnConfigs: ColumnConfig<User>[] = [
 *   { id: 'name', header: 'Name', accessor: 'name' },
 *   {
 *     id: 'platforms',
 *     header: 'IDEs/Platform',
 *     children: [
 *       { id: 'platforms.covered', header: 'Covered', accessor: 'clients', renderCell: ... },
 *       { id: 'platforms.supported', header: 'Supported', accessor: 'clients', renderCell: ... },
 *     ],
 *   },
 * ];
 *
 * const columns = useDataTableColumns(columnConfigs);
 * // Returns 3 TanStack columns: name, platforms.covered, platforms.supported
 * ```
 */
export function useDataTableColumns<TRow extends TableRowData>(
  columnConfigs: ColumnConfig<TRow>[],
  resizable = false
): ColumnDef<TRow>[] {
  // Timestamp columns size from the per-user Timestamp Format and display
  // zone. Subscribes to the width, not the zone, so the table re-renders only
  // when the width changes; timestamp cells follow the zone themselves.
  const { value: timestampFormat } = useContext(TimestampFormatContext);
  const timestampWidth = useSyncExternalStore(subscribeDisplayTimeZone, () =>
    getTimestampColumnWidth(timestampFormat, getDisplayTimeZone())
  );
  return useMemo(
    () =>
      getLeafColumns(columnConfigs).map(config =>
        convertColumnConfig(config, timestampWidth, resizable)
      ),
    [columnConfigs, timestampWidth, resizable]
  );
}

function convertColumnConfig<TRow extends TableRowData>(
  config: ColumnConfig<TRow>,
  timestampWidth: number,
  resizable: boolean
): ColumnDef<TRow> {
  const {
    accessor,
    description,
    renderCell,
    width,
    minWidth,
    maxWidth,
    clipOverflow,
    timestamp,
    fill,
    ...baseConfig
  } = config;

  // Build the column definition with accessor handling
  const columnDef: ColumnDef<TRow> = {
    ...baseConfig,
    ...getAccessorDefinition(accessor),
  };

  if (timestamp) {
    // Pinned min = max = size: `auto` layout reads an unset max as "grow" and
    // an unset min as "no floor" (buildColumnSizeStyle). `minWidth` raises the
    // width (e.g. for a header wider than the value) rather than setting a
    // separate min, which getSize() would clamp back down to the max. `width`
    // and `maxWidth` don't apply: a max below the size would undo the floor.
    const timestampSize = Math.max(timestampWidth, minWidth ?? 0);
    columnDef.size = timestampSize;
    columnDef.minSize = timestampSize;
    // In a resizable table the user may drag it wider (the floor still keeps
    // the timestamp whole); the cap only serves `auto` layout's grow rule.
    if (!resizable) {
      columnDef.maxSize = timestampSize;
    }
  } else {
    if (typeof width === 'number') {
      columnDef.size = width;
    }
    if (typeof minWidth === 'number') {
      columnDef.minSize = minWidth;
    } else if (
      resizable &&
      clipOverflow === false &&
      typeof width === 'number'
    ) {
      // Unclipped content (action buttons, pills) is sized by its author, so
      // a user can't drag it narrower — that cuts the controls off. Only where
      // the user resizes: in `auto` layout a floor would stop it shrinking.
      columnDef.minSize = width;
    }
    if (typeof maxWidth === 'number') {
      columnDef.maxSize = maxWidth;
    }
  }

  // Dragging the edge of a fill column changes nothing visible, so offer no
  // handle on it.
  if (fill) {
    columnDef.enableResizing = false;
  }

  // Convert renderCell to TanStack cell format if provided
  if (renderCell) {
    columnDef.cell = createCustomCellRenderer(renderCell, config);
  }

  // Add metadata if description is provided
  if (description !== undefined) {
    columnDef.meta = { ...columnDef.meta, description };
  }

  columnDef.meta = {
    ...columnDef.meta,
    clipOverflow: clipOverflow ?? !(timestamp || isDateColumn(config)),
    ...(fill ? { fill: true } : {}),
  };

  return columnDef;
}

function isDateColumn<TRow extends TableRowData>(
  config: ColumnConfig<TRow>
): boolean {
  return config.filterable === true && config.dataType === ColumnDataTypes.DATE;
}

function getAccessorDefinition<TRow extends TableRowData>(
  accessor?: keyof TRow | ((row: TRow) => unknown)
): Partial<ColumnDef<TRow>> {
  if (!accessor) {
    return {};
  }
  if (typeof accessor === 'function') {
    return { accessorFn: accessor as (row: TRow) => unknown };
  }
  return { accessorKey: accessor as keyof TRow };
}

function createCustomCellRenderer<TRow extends TableRowData>(
  customCell: (context: CustomCellContext<TRow>) => React.ReactNode,
  config: ColumnConfig<TRow>
) {
  return (info: {
    getValue: () => unknown;
    row: { original: TRow; index: number };
  }) => {
    return customCell({
      value: info.getValue(),
      row: info.row.original,
      column: config,
      rowIndex: info.row.index,
    });
  };
}
